import { useState, FormEvent } from 'react';
import { submitEmailForDeals } from '../services/emailService';
import './EmailCapture.css';

export interface EmailCaptureProps {
  buttonText?: string;
  placeholder?: string;
  showMicroCopy?: boolean;
  className?: string;
  onSuccess?: () => void;
}

export function EmailCapture({
  buttonText = 'Get Exclusive Deals',
  placeholder = 'Enter your email',
  showMicroCopy = true,
  className = '',
  onSuccess,
}: EmailCaptureProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await submitEmailForDeals(email.trim());
      
      if (result.success) {
        setSuccess(true);
        setEmail('');
        if (onSuccess) {
          onSuccess();
        }
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`email-capture ${className}`}>
      <form onSubmit={handleSubmit} className="email-capture-form">
        <div className="email-capture-input-group">
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
              setSuccess(false);
            }}
            placeholder={placeholder}
            className="email-capture-input"
            disabled={loading || success}
            required
            aria-label="Email address"
          />
          <button
            type="submit"
            className="email-capture-button"
            disabled={loading || success || !email.trim()}
          >
            {loading ? 'Subscribing...' : success ? '✓ Subscribed!' : buttonText}
          </button>
        </div>
        {error && (
          <div className="email-capture-error" role="alert">
            {error}
          </div>
        )}
        {success && (
          <div className="email-capture-success" role="alert">
            Successfully subscribed! Check your email for confirmation.
          </div>
        )}
        {showMicroCopy && !success && (
          <p className="email-capture-microcopy">
            No spam. Unsubscribe anytime. Deals only.
          </p>
        )}
      </form>
    </div>
  );
}
